import { useCallback, useState, type ReactNode } from 'react';
import type { User } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/components/Toast';

export interface ResetPasswordModalProps {
  target: User;
  onClose: () => void;
}

interface ResetResult {
  temporaryPassword: string;
  expiresAt: string;
  name: string;
  email: string;
  /** False when the heads-up email could not be queued — the reset still happened. */
  noticeSent: boolean;
}

/**
 * Issue a temporary password to somebody who cannot get in.
 *
 * Two states: a warning about what the reset does, then the password itself.
 * The second state is the only time that password is ever readable — the server
 * stores a hash and has no way to produce it again — so the dialog says so
 * plainly and offers a copy button rather than hoping the admin selects it
 * cleanly before closing.
 *
 * Deliberately absent: any "email it to them" button. Mail keeps a copy in two
 * mailboxes indefinitely, which is the wrong place for a live credential. The
 * employee is emailed that the reset happened; the password travels by whatever
 * channel the administrator already uses to reach them.
 */
export function ResetPasswordModal({ target, onClose }: ResetPasswordModalProps): ReactNode {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResetResult | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.admin.resetPassword<ResetResult>({ userId: target.id });
      setResult(res);
      toast(`Temporary password issued for ${res.name}.`, 'ok');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [target.id, toast]);

  const copy = useCallback(async (): Promise<void> => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
    } catch {
      // Clipboard access is refused in some browsers and over plain HTTP. The
      // password is on screen either way, so this is a convenience failing, not
      // the action failing.
      setError('Could not copy automatically — select the password above instead.');
    }
  }, [result]);

  if (result) {
    const expires = new Date(result.expiresAt);
    return (
      <Modal title="Temporary password issued" onClose={onClose}>
        <p style={{ fontSize: 12.5 }}>
          Give this to <b>{result.name}</b> yourself. It is shown here once and cannot be
          retrieved again — if you lose it, run the reset a second time.
        </p>

        <div className="temp-password">
          <code>{result.temporaryPassword}</code>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <ul style={{ fontSize: 12, lineHeight: 1.7, paddingLeft: 18, margin: '12px 0' }}>
          <li>
            It stops working at <b>{expires.toLocaleString()}</b>. After that they sign in with an
            emailed code, or you issue a new one.
          </li>
          <li>They must choose their own password the moment they sign in with it.</li>
          <li>
            {result.name} has been signed out everywhere
            {result.noticeSent
              ? ', and emailed to say the reset happened — the password itself is not in that email.'
              : '.'}
          </li>
        </ul>

        {result.noticeSent ? null : (
          <p className="panel-note">
            The email telling {result.name} their password was reset could not be sent. The reset
            itself went through — tell them yourself, so an account change they did not ask for
            does not go unnoticed.
          </p>
        )}

        {error ? <div className="error-text">{error}</div> : null}

        <div className="cf-submitbar">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Reset Password" onClose={busy ? () => undefined : onClose}>
      <p style={{ fontSize: 12.5 }}>
        This issues a one-day temporary password for <b>{target.name}</b> ({target.email}) and
        shows it to you once, to pass on however you normally reach them.
      </p>
      <p className="panel-note">
        Their current password stops working immediately and they are signed out on every device.
        They will be asked to choose a new password as soon as they sign in. The reset is recorded
        in the audit trail under your name.
      </p>

      {error ? <div className="error-text">{error}</div> : null}

      <div className="cf-submitbar">
        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => void submit()}
          disabled={busy}
        >
          {busy ? <Spinner /> : null}
          Reset {target.name}&apos;s password
        </button>
      </div>
    </Modal>
  );
}
